import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink } from '@mui/material';
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import { useContactLinkContext } from 'contexts/ContactLinkContext'; // Import the ContactLinkContext
import placeholderImage from 'images/placeholder-grey-image.jpg'; // Adjust the path as needed
import avatarPlaceholder from 'images/avatar-cropped.png'; // Adjust the path as needed
import getIconForType from 'utils/icons/getIconForType'; // Import getIconForType to get the icon based on type
import { height, lighten } from '@mui/system'; // Import the lighten utility
import { useParams, useNavigate } from 'react-router-dom';
// getContactLinksByCard import removed - using ContactLinkContext instead
import TeamLeadCaptureOverlayPreview from 'team/components/editMember/leadCapture/TeamLeadCaptureOverlayPreview'; // Import the TeamLeadCaptureOverlayPreview
import { validateUUID } from 'utils/validator';
import { OpenInNewIcon } from 'icons/materialIcons';

import { auto } from '@cloudinary/url-gen/actions/resize';
import { getTeamDesignComponent } from 'team/components/cardDesignTemplates/index';
import { ThemeProvider } from '@mui/material/styles';
import { cardDesign5PreviewTheme } from 'team/shared/themes';

const TeamMemberDigitalCardPreview = ({ cardId: propCardId = null, contactLink = null, contactLinks: propContactLinks = null, disableLeadCapture = false, cardData = null }) => {
    const { cardId: paramCardId } = useParams();
    const cardId = propCardId || paramCardId || "tempCardId"; // Use prop first, then URL param, then fallback

    // Use context to get card details and contact links
    const { cardState, loadCardFromServer, updateCardData, isTemporaryCard, setCardFont } = useCardContext();
    const { contactLinks: contextContactLinks, setCurrentCard, contactLinksByCard } = useContactLinkContext(); // Get contact links from context
    const [overlayOpen, setOverlayOpen] = useState(false); // Overlay state
    const [visibleContactLinks, setVisibleContactLinks] = useState([]);

    // ✅ CRITICAL FIX: Use ref to track if fresh data has been loaded to prevent infinite loops
    const freshDataLoadedRef = useRef(new Set());
    const isLoadingRef = useRef(false);

    const customLinkTypeLabels = cardState[cardId]?.customLinkTypeLabels || {};

    const navigate = useNavigate(); // Initialize useNavigate

    // Get card data directly from context to ensure re-renders, with fallback to cardData prop
    const currentCard = cardData || cardState[cardId] || {};
    const cardFullName = currentCard.cardFullName;
    const cardJobTitle = currentCard.cardJobTitle;
    const cardCompany = currentCard.cardCompany;
    const cardTheme = currentCard.cardTheme;
    const cardProfilePictureUrl = currentCard.cardProfilePictureUrl;
    const cardCoverPhotoUrl = currentCard.cardCoverPhotoUrl;
    const cardCompanyLogoUrl = currentCard.cardCompanyLogoUrl;
    const contextCardLayout = currentCard.contextCardLayout;
    const cardLinkColor = currentCard.cardLinkColor;
    const leadCaptureModeOn = currentCard.leadCaptureModeOn ?? false;
    const cardLocation = currentCard.cardLocation;
    const cardBio = currentCard.cardBio;
    const cardPronoun = currentCard.cardPronoun || 'he';
    const cardFont = currentCard.cardFont || 'Inter';

    // ✅ ADD: Use cardData prop when provided, fallback to context data
    // CRITICAL: For template preview (cardId="template-preview"), prioritize cardData prop
    // because templates use TeamTemplateContext, not CardContext
    const finalCardData = useMemo(() => ({
        cardFullName: cardData?.cardFullName || cardFullName,
        cardJobTitle: cardData?.cardJobTitle || cardJobTitle,
        cardCompany: cardData?.cardCompany || cardCompany,
        cardPronoun: cardData?.cardPronoun || cardPronoun,
        cardTheme: cardData?.cardTheme || cardTheme,
        cardProfilePictureUrl: cardData?.cardProfilePictureUrl || cardProfilePictureUrl,
        cardCoverPhotoUrl: cardData?.cardCoverPhotoUrl || cardCoverPhotoUrl,
        // CRITICAL: For template preview, use cardData prop directly (don't fallback to context)
        // because template context is different from card context
        // Match the pattern used for profile photo and cover photo - no special null handling
        cardCompanyLogoUrl: cardId === 'template-preview'
            ? (cardData?.cardCompanyLogoUrl || '')
            : (cardData?.cardCompanyLogoUrl || cardCompanyLogoUrl),
        contextCardLayout: cardData?.contextCardLayout || contextCardLayout,
        cardLinkColor: cardData?.cardLinkColor || cardLinkColor,
        cardLocation: cardData?.cardLocation || cardLocation,
        cardBio: cardData?.cardBio || cardBio,
        cardFont: cardData?.cardFont || cardFont,
        customLinkTypeLabels: cardData?.customLinkTypeLabels || customLinkTypeLabels,
        leadCaptureInputFields: cardData?.leadCaptureInputFields || currentCard?.leadCaptureInputFields
    }), [cardData, cardFullName, cardJobTitle, cardCompany, cardPronoun, cardTheme, cardProfilePictureUrl, cardCoverPhotoUrl, cardCompanyLogoUrl, contextCardLayout, cardLinkColor, cardLocation, cardBio, cardFont, customLinkTypeLabels, currentCard, cardId]);

    // ✅ ADD: Debug logging for cardData and finalCardData
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('🔍 TeamMemberDigitalCardPreview: cardData prop received:', cardData);
            console.log('🔍 TeamMemberDigitalCardPreview: Context data available:', {
                cardFullName,
                cardJobTitle,
                cardCompany,
                cardPronoun,
                cardTheme,
                cardCompanyLogoUrl
            });
            console.log('🔍 TeamMemberDigitalCardPreview: finalCardData constructed:', finalCardData);
            console.log('🔍 TeamMemberDigitalCardPreview: Company logo details:', {
                cardId,
                isTemplatePreview: cardId === 'template-preview',
                cardDataCompanyLogoUrl: cardData?.cardCompanyLogoUrl,
                contextCompanyLogoUrl: cardCompanyLogoUrl,
                finalCompanyLogoUrl: finalCardData.cardCompanyLogoUrl
            });
        }
    }, [cardData, finalCardData, cardFullName, cardJobTitle, cardCompany, cardPronoun, cardTheme, cardCompanyLogoUrl, cardId, cardFont]);

    // Set current card in context when component mounts - THIS MUST BE FIRST
    // Skip if cardData is provided (preview mode) or template-preview
    useEffect(() => {
        if (cardId && !cardData && cardId !== 'template-preview') {
            setCurrentCard(cardId);
        }
    }, [cardId, setCurrentCard, cardData]);

    // ✅ CRITICAL FIX: Memoized fresh data loading function to prevent infinite loops
    const loadFreshCardData = useCallback(async () => {
        // ✅ FIXED: Skip API call if cardData prop is provided (preview mode) or if card is temporary
        if (cardData || !cardId || isTemporaryCard(cardId) || freshDataLoadedRef.current.has(cardId) || isLoadingRef.current) {
            return;
        }

        // ✅ FIXED: Skip API call for template preview cardId
        if (cardId === 'template-preview') {
            return;
        }

        try {
            isLoadingRef.current = true;

            // Load fresh data from server using CardContext method
            const response = await loadCardFromServer(cardId);

            if (response && updateCardData) {
                // Get server lead capture mode value
                const serverLeadCaptureMode = Boolean(response.leadCaptureModeOn);

                // Force update CardContext with server values using updateCardData
                await updateCardData(cardId, {
                    leadCaptureModeOn: serverLeadCaptureMode,
                    autoFollowUpIntroEmail: Boolean(response.autoFollowUpIntroEmail),
                    leadCaptureInputFields: response.leadCaptureInputFields || [], // Update input fields including dropdowns
                    // Also update other fields to ensure consistency
                    cardFullName: response.contact_info?.fullName || '',
                    cardJobTitle: response.contact_info?.jobTitle || '',
                    cardCompany: response.contact_info?.company || '',
                    cardPronoun: response.contact_info?.pronoun || 'he',
                    cardTheme: response.cardTheme || '',
                    cardProfilePictureUrl: response.profilePictureUrl || '',
                    cardCoverPhotoUrl: response.coverPhotoUrl || '',
                    cardCompanyLogoUrl: response.companyLogoUrl || '',
                    contextCardLayout: response.cardLayout || 'center-aligned',
                    cardLinkColor: response.linkColor || '',
                    cardLocation: response.contact_info?.location || '',
                    cardBio: response.bio || '',
                    cardFont: response.font || 'Inter',
                    customLinkTypeLabels: response.customLinkTypeLabels || {
                        'Contact': 'Contact',
                        'Social': 'Social',
                        'Products & Services': 'Products & Services'
                    }
                }, false); // false = don't sync back to server, just update local context
                
                // Also update font directly using setCardFont if available
                if (setCardFont && response.font) {
                    setCardFont(cardId, response.font);
                }

                // ✅ FIXED: Mark as loaded to prevent re-loading
                freshDataLoadedRef.current.add(cardId);

                // ✅ FIXED: Clear the loaded flag after 5 minutes to allow eventual refresh
                setTimeout(() => {
                    freshDataLoadedRef.current.delete(cardId);
                }, 5 * 60 * 1000);
            }
        } catch (error) {
            // Error loading fresh card data
        } finally {
            isLoadingRef.current = false;
        }
    }, [cardId, isTemporaryCard, loadCardFromServer, updateCardData, cardData, setCardFont]);

    // ✅ FIXED: Load fresh card data only once per card
    useEffect(() => {
        loadFreshCardData();
    }, [loadFreshCardData]);

    // Update visibleContactLinks whenever contextContactLinks or contactLink prop changes
    useEffect(() => {
        // Ensure context is ready and current card is set
        // Skip if cardData is provided (preview mode) or template-preview
        if (!cardId || cardData || cardId === 'template-preview') {
            // For preview mode, use propContactLinks if provided
            if (cardData && propContactLinks) {
                setVisibleContactLinks(propContactLinks);
            } else {
                setVisibleContactLinks([]);
            }
            return;
        }

        // ✅ FIXED: Smart link combination - same logic as LinksContainer
        let baseLinks;
        const isTemporaryCard = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

        if (isTemporaryCard) {
            // ✅ NEW CARD CREATION: Combine links from both sources
            const currentCardLinks = contextContactLinks || [];
            const tempCardLinks = contactLinksByCard['tempCardId'] || [];
            baseLinks = [...currentCardLinks, ...tempCardLinks];
        } else {
            // ✅ EXISTING CARD: Use only database links
            baseLinks = contextContactLinks && contextContactLinks.length > 0
                ? contextContactLinks
                : (propContactLinks || []);
        }

        let links = [...baseLinks];
        if (contactLink) {
            // Check if the preview link already exists in the contactLinks array
            const linkExists = links.some(link =>
                link.type === contactLink.type &&
                link.details?.linkUrl === contactLink.details?.linkUrl
            );
            if (!linkExists) {
                // Add the preview link with isVisible set to true
                const previewLinkWithVisibility = {
                    ...contactLink,
                    isVisible: true,
                    // Remove hardcoded categorization - let the card design handle it
                };
                links = [...links, previewLinkWithVisibility];
            }
        }

        const visibleLinks = links.filter(link => link.isVisible !== false);
        setVisibleContactLinks(visibleLinks);
    }, [contextContactLinks, propContactLinks, contactLink, cardId, contactLinksByCard, cardData]);

    const handleNavigate = () => {
        navigate(`/card/${cardId}`); // Navigate to the card details page
    };

    // Lighten the card theme color
    const lightenedCardTheme = cardTheme ? lighten(cardTheme, 0.7) : '#fff';

    // Effect to manage overlay visibility based on leadCaptureModeOn (only if not disabled)
    useEffect(() => {
        if (!disableLeadCapture) {
            setOverlayOpen(leadCaptureModeOn);
        } else {
            setOverlayOpen(false); // Force close overlay when disabled
        }
    }, [leadCaptureModeOn, disableLeadCapture]);

    const handleCloseOverlay = () => {
        setOverlayOpen(false); // Close overlay
    };

    const formatPronounDisplay = (pronoun) => {
        switch (pronoun) {
            case 'he': return 'he/him';
            case 'she': return 'she/her';
            case 'they': return 'they/them';
            case 'ze': return 'ze/zir';
            case '': return '';
            case 'custom': return 'custom'; // This should rarely happen with the new implementation
            default:
                // For custom pronouns, return the custom value itself
                return pronoun;
        }
    };

    const cardDesign = 'design5'; // Later you can get this from card settings
    const CardDesignComponent = getTeamDesignComponent(cardDesign);

    // Load font when cardFont changes
    useEffect(() => {
        const fontToLoad = finalCardData.cardFont || 'Inter';
        console.log('🔍 [TeamMemberDigitalCardPreview] Loading font:', {
            cardId,
            fontToLoad,
            finalCardDataFont: finalCardData.cardFont,
            currentCardFont: currentCard.cardFont
        });
        if (fontToLoad && fontToLoad !== 'Inter') {
            import('utils/googleFontsApi').then(({ loadGoogleFont }) => {
                // Variants will be automatically determined from the curated fonts list
                loadGoogleFont(fontToLoad).then(() => {
                    console.log('✅ [TeamMemberDigitalCardPreview] Font loaded successfully:', fontToLoad);
                }).catch(err => {
                    console.warn('❌ [TeamMemberDigitalCardPreview] Failed to load font:', fontToLoad, err);
                });
            });
        }
    }, [finalCardData.cardFont, cardId, currentCard.cardFont]);

    return (
        <ThemeProvider theme={cardDesign5PreviewTheme}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: '330px',
                    margin: 'auto',
                    height: '600px',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    scrollbarWidth: 'thin',
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        marginBottom: 2,
                        color: "#828282",
                        fontSize: "0.8rem",
                        fontWeight: "600"
                    }}
                >
                    Card Live Preview
                </Typography>

                {/* Hide "View Card" button for template preview */}
                {cardId !== 'template-preview' && (
                    <Button
                        component="a"
                        href={`/card/${cardId}?source=preview&track=false`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#2aaff7',
                            textDecoration: 'none',
                            marginBottom: 2,
                            padding: '6px 12px',
                            fontSize: "0.8rem",
                            fontWeight: "600",
                            textTransform: 'none',
                            borderRadius: '4px',
                            zIndex: 5,
                            '&:hover': {
                                backgroundColor: 'rgba(42, 175, 247, 0.08)',
                                textDecoration: 'underline'
                            }
                        }}
                        endIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem" }} />}
                    >
                        View Card
                    </Button>
                )}

                <Box
                    sx={{
                        width: '100%',
                        position: 'relative'
                    }}
                >
                    <CardDesignComponent
                        cardFullName={finalCardData.cardFullName || 'Name Placeholder'}
                        cardJobTitle={finalCardData.cardJobTitle || ''}
                        cardCompany={finalCardData.cardCompany || ''}
                        cardPronoun={finalCardData.cardPronoun || 'he'}
                        formattedPronoun={formatPronounDisplay(finalCardData.cardPronoun || 'he')}
                        cardTheme={finalCardData.cardTheme || ''}
                        cardProfilePictureUrl={finalCardData.cardProfilePictureUrl || avatarPlaceholder}
                        cardCoverPhotoUrl={finalCardData.cardCoverPhotoUrl || placeholderImage}
                        cardCompanyLogoUrl={finalCardData.cardCompanyLogoUrl || null}
                        contextCardLayout={finalCardData.contextCardLayout || 'center-aligned'}
                        cardLinkColor={finalCardData.cardLinkColor || ''}
                        cardLocation={finalCardData.cardLocation || ''}
                        cardBio={finalCardData.cardBio || ''}
                        cardFont={finalCardData.cardFont || 'Inter'}
                        visibleContactLinks={visibleContactLinks || []}
                        previewContactLink={contactLink} // Pass preview link for real-time updates
                        handleSaveContact={() => { }}
                        customLinkTypeLabels={finalCardData.customLinkTypeLabels} // Empty function since it's just a preview
                    />

                    {/* CRITICAL FIX: Add LeadCaptureOverlayPreview */}
                    {overlayOpen && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                                paddingTop: { xs: '80px', sm: '100px', md: '120px' }
                            }}
                        >
                            <TeamLeadCaptureOverlayPreview
                                onClose={handleCloseOverlay}
                                onSuccess={() => { }}
                                addMoreConnectButtonColor={cardLinkColor || '#2f82ed'}
                                triggerSource="preview"
                                width="260px"
                                height="auto"
                                inputFields={finalCardData.leadCaptureInputFields}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default TeamMemberDigitalCardPreview;